import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Shared access model for gated portal features (Shop, Clip Studio, Voice
 * Studio): a global mode in `admin_settings` plus a per-user profile flag used
 * by "specific". Platform admins always pass. The matching edge-function gate
 * lives in supabase/functions/_shared/featureAccess.ts.
 */
export type FeatureVisibilityMode = "admins" | "all" | "specific";
export type FeatureProfileFlag = "shop_enabled" | "clip_studio_enabled" | "voice_studio_enabled" | "delivery_status_enabled" | "events_enabled";

export const featureVisibilityQueryKey = (settingKey: string) =>
  ["feature-visibility-mode", settingKey] as const;

function isMode(value: unknown): value is FeatureVisibilityMode {
  return value === "admins" || value === "all" || value === "specific";
}

/** Global mode for one feature. Readable by any signed-in user. */
export function useFeatureVisibilityMode(settingKey: string) {
  const { data, isLoading } = useQuery<FeatureVisibilityMode>({
    queryKey: featureVisibilityQueryKey(settingKey),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("value")
        .eq("key", settingKey)
        .maybeSingle();
      if (error) return "admins";
      return isMode(data?.value) ? data.value : "admins";
    },
  });
  // Most restrictive until the row resolves.
  return { mode: data ?? "admins", isLoading };
}

export function useFeatureAccess(
  settingKey: string,
  profileFlag: FeatureProfileFlag,
): { hasAccess: boolean; isLoading: boolean } {
  const { hasRole, profile } = useAuth();
  const { mode, isLoading } = useFeatureVisibilityMode(settingKey);

  if (hasRole("admin")) return { hasAccess: true, isLoading: false };
  if (isLoading) return { hasAccess: false, isLoading: true };
  if (mode === "all") return { hasAccess: true, isLoading: false };
  if (mode === "specific") {
    const flags = profile as Partial<Record<FeatureProfileFlag, boolean>> | null;
    return { hasAccess: flags?.[profileFlag] === true, isLoading: false };
  }
  return { hasAccess: false, isLoading: false };
}
